import React, { useState } from 'react';
import useGetCarDealer from '../../../hooks/useGetCarDealer';
import { ClipLoader } from 'react-spinners';
import baseService from '../../../services/baseService';
import { Link } from 'react-router-dom';

const DealerInfo = () => {
  const { dealerData, dealerLoading, dealerError } = useGetCarDealer('/car-dealer/get-car-dealer'); // adjust API endpoint as needed
  const [formData, setFormData] = useState({
    buyerFeeDetails: '',
    dealershipName: '',
    dealershipWebsite: '',
    vehicleSalesEachMonth: '',
    dealershipLicense: null
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');

  // Set initial form data when dealer data is loaded
  React.useEffect(() => {
    if (dealerData) {
      setFormData({
        buyerFeeDetails: dealerData?.data?.buyerFeeDetails || '',
        dealershipName: dealerData?.data?.dealershipName || '',
        dealershipWebsite: dealerData?.data?.dealershipWebsite || '',
        vehicleSalesEachMonth: dealerData?.data?.vehicleSalesEachMonth || '',
        dealershipLicense: null
      });
      setPreviewImage(dealerData?.data?.dealershipLicense || null);
    }
  }, [dealerData]);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        dealershipLicense: file
      }));
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setUpdateMessage('');

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });

      await baseService.put('car-dealer/update-car-dealer', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUpdateMessage('Dealer information updated successfully!');
    } catch (error) {
      setUpdateMessage('Failed to update dealer information. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (dealerLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <ClipLoader color="#EF4444" size={50} />
      </div>
    );
  }

  if (!dealerData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-4">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">No Dealer Information Found</h2>
          <p className="text-gray-600">
            To register as a dealer, go to <Link to={"/upload-local-car"} className='underline hover:text-blue-800'>sell you car</Link> and upload your car with dealer information.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[85vw] mx-auto p-[2vw] my-[2vw] rounded-lg shadow-md">
    <h2 className="text-left text-[24px] md:text-[36px] lg:text-[2.3vw] font-urbanist mb-[1vw] font-bold">Update Dealer Information</h2>
    
    <form onSubmit={handleSubmit} className="space-y-[2vw]">
      <div className="space-y-[2vw]">
        <div>
          <label className="block text-left text-[1.2vw] font-medium text-gray-700">Additional Fees</label>
          <textarea
            name="buyerFeeDetails"
            value={formData.buyerFeeDetails}
            onChange={handleInputChange}
            className="mt-[1vw] block w-full rounded-lg border-gray-300 border shadow-sm focus:border-red-500 focus:ring-red-500 p-[1vw] text-[1vw]"
            rows="3"
          />
        </div>

        <div>
          <label className="block text-left text-[1.2vw] font-medium text-gray-700">Dealership Name</label>
          <input
            type="text"
            name="dealershipName"
            value={formData.dealershipName}
            onChange={handleInputChange}
            className="mt-[1vw] block w-full rounded-lg border-gray-300 border shadow-sm focus:border-red-500 focus:ring-red-500 p-[1vw] text-[1vw]"
          />
        </div>

        <div>
          <label className="block text-left text-[1.2vw] font-medium text-gray-700">Dealership Website</label>
          <input
            type="url"
            name="dealershipWebsite"
            value={formData.dealershipWebsite}
            onChange={handleInputChange}
            className="mt-[1vw] block w-full rounded-lg border-gray-300 border shadow-sm focus:border-red-500 focus:ring-red-500 p-[1vw] text-[1vw]"
          />
        </div>

        <div>
          <label className="block text-left text-[1.2vw] font-medium text-gray-700">Monthly Vehicles</label>
          <select
            name="vehicleSalesEachMonth"
            value={formData.vehicleSalesEachMonth}
            onChange={handleInputChange}
            className="mt-[1vw] block w-full rounded-lg border-gray-300 border shadow-sm focus:border-red-500 focus:ring-red-500 p-[1vw] text-[1vw]"
          >
            <option value="">Select sales range</option>
            <option value="Less than 10">1-10</option>
            <option value="10-50">11-20</option>
            <option value="50-100">21-50</option>
            <option value="More than 100">50+</option>
          </select>
        </div>

        <div>
          <label className="block text-left text-[1.2vw] font-medium text-gray-700">Dealer License</label>
          <div className="mt-[1vw] flex items-center space-x-[2vw]">
            {previewImage && (
              <div className="relative">
                <img
                  src={previewImage}
                  alt="Dealer License"
                  className="h-[8vw] w-auto object-cover rounded-lg shadow-md"
                />
              </div>
            )}
            <div className="flex-1">
              <input
                type="file"
                name="dealershipLicense"
                onChange={handleImageChange}
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                id="dealershipLicense"
              />
              <label
                htmlFor="dealershipLicense"
                className="cursor-pointer inline-flex items-center px-[1.5vw] py-[1vw] border border-gray-300 rounded-lg shadow-sm text-[1vw] font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Change Image
              </label>
              <p className="mt-[0.5vw] text-[0.8vw] text-gray-500">PNG, JPG, JPEG or WEBP. Max 180mb.</p>
            </div>
          </div>
        </div>
      </div>

      {updateMessage && (
        <div className={`p-[2vw] rounded-lg ${updateMessage.includes('success') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {updateMessage}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center px-[1.8vw] py-[0.8vw] border border-transparent rounded-lg shadow-lg text-[1.2vw] font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
        >
          {isSubmitting ? (
            <>Updating...</>
          ) : (
            'Update Dealer Information'
          )}
        </button>
      </div>
    </form>
  </div>
  );
};

export default DealerInfo;
